import type Dexie from 'dexie';

/**
 * **Product migration v002 — `Hypothesis.createdAt`, backfilled `0`.**
 *
 * Design §3.2's ruling and §13.3's specification, verbatim in effect: *"Adds `createdAt: number` to
 * the `hypotheses` store, backfilling **`0`** for every existing row. It touches no other store,
 * needs no `reconcileNeeded` flag (nothing derived changes), and imports no `domain/`."*
 *
 * ── What it is for, which is a live defect and not a tidy-up ─────────────────
 *
 * DISC-4/LOCK-2 sort by `(cachedCount desc, firstObservationCreatedAt asc, createdAt asc, id asc)`.
 * **Before kickoff every row ties on the first two terms** — no increments, so no counts and no
 * first observations — and `order.ts` had no third term to read, so the whole board fell through to
 * `id asc`. Ids are `crypto.randomUUID()` (§11.7, deliberately not ULIDs), so the pre-kickoff board
 * came up in *random* order every time it was rendered. §6.1: *"Term 3 is not decoration."*
 *
 * ── Why the backfill is `0` and not a plausible timestamp ────────────────────
 *
 * `0` is the value U-8's `HYPOTHESIS_CREATED_AT_UNAVAILABLE` sentinel already supplied at the
 * projection site, so a pre-`v002` row keeps sorting **exactly** as it did: tied at term 3, falling
 * through to `id`. That makes the migration behaviour-preserving *by construction* rather than by
 * an assertion someone has to remember to write. U-8 declined to fabricate a creation time for rows
 * whose creation time the database never held, and this migration declines it for the same reason —
 * a plausible timestamp would silently reorder every existing match's board and there would be no
 * way to tell the invented values from the real ones afterwards.
 *
 * The test for this is behavioural, not structural (§13.3): not *"the column exists"* but *"the
 * v001 fixture's Discovery order is identical before and after `v002`"*, plus *"a row inserted after
 * `v002` with a later `createdAt` sorts after a backfilled row it ties with on count and first
 * observation."* Both live in `fixtureMigration.test.ts`.
 *
 * ── No `.stores()` call, deliberately ───────────────────────────────────────
 *
 * The index set is unchanged, and §3.4/`v001_initial.ts` are explicit that `hypotheses`' indexes are
 * *"exactly the index set build unit U-0 measured against"* — an index added here that U-0 did not
 * measure invalidates A-1's number and is a decision to re-run U-0, not a free lookup. `createdAt`
 * is read only as a comparator's last-resort tie-break over rows already in hand, never as a query
 * key, so it needs none. Dexie inherits the previous version's schema when `stores()` is omitted.
 *
 * NFR-7 is forward-only and `v001_initial.ts` is never edited once it has run on a device.
 */
export function v002HypothesisCreatedAt(db: Dexie): void {
  db.version(2).upgrade(async (tx) => {
    await tx
      .table('hypotheses')
      .toCollection()
      .modify((row: { createdAt?: number }) => {
        row.createdAt = 0;
      });
  });
}
